import React from "react";
import DOMPurify from "dompurify";
import styled from "styled-components";
import { colors, links } from "../common/mixins.js";
import { Warning } from "../icons/Warning";
import { InfoIcon } from "../icons/InfoIcon";

const StyledIcon = styled("i")((props) => ({
  display: "flex",
  alignItems: "center",
  width: props.width || "24px",
  height: props.height || "24px",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  ...(props.kind === "warning-colored" && {
    backgroundImage:
      'url("/Site/images/icons/order-details/warning-colored.svg")',
  }),
}));

const Container = styled("div")(
  ({ isDelayedTicketDelivery, isGovWalletInfo, isUnpaidServiceFee, $isError }) => ({
    width: "100%",
    display: "flex",
    gap: "8px",
    alignItems: "center",
    padding: $isError ? "12px 12px 12px 8px" : "16px",
    borderRadius: "8px",
    backgroundColor: $isError
      ? "#FFEAEB"
      : isUnpaidServiceFee
        ? colors.warningAlert
        : isDelayedTicketDelivery
          ? colors.delayedDeliveryBg
          : isGovWalletInfo
            ? "#fff"
            : colors.warningAlert,
    color: isDelayedTicketDelivery ? colors.delayedDeliveryColor : "#1f1c43",
    fontSize: "14px",
    fontWeight: "400",
    justifyContent: "space-between",
    border: $isError
      ? "1px solid #FC2D33"
      : isUnpaidServiceFee
        ? "1px solid #fcb041"
        : isDelayedTicketDelivery
          ? `1px solid ${colors.delayedDeliveryBorder}`
          : "1px solid #fcb041",
    fontFamily: `"pf_highway_sans_pro_lightRg", sans-serif`,
    "@media (max-width: 768px)": {
      flexWrap: "wrap",
    },
  }),
);

const PayButton = styled("button")({
  minWidth: 90,
  height: 37,
  fontSize: 14,
  borderRadius: 24,
  backgroundColor: "#FFFFFF",
  border: "1px solid #EDF1F6",
  fontFamily: `"pf_highway_sans_pro_lightRg", sans-serif`,
  padding: "8px 16px",
  fontWeight: 600,
  color: colors.sfTextColor,
  textDecoration: "var(--linkDecoration)",
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "auto",
  minWidth: "auto",
  flexShrink: 0,

  "&:hover": {
    border: "1px solid #D8DBE0",
  },
  "&:active": {
    border: "1px solid #EDF1F6",
    backgroundColor: "#F7F9FB",
  },
});

const Content = styled("div")({
  display: "flex",
  gap: 8,
  "& > :first-child": {
    flexShrink: 0,
  },
  "@media (max-width: 768px)": {
    alignItems: "start",
  },
});

const Title = styled("div")(({ isGovWalletInfo }) => ({
  fontWeight: 600,
  marginBottom: 4,
  ...(isGovWalletInfo && { fontSize: "20px" }),
}));

const Description = styled("div")`
  padding-right: 16px;
  ${({ isGovWalletInfo }) => isGovWalletInfo && "font-size: 16px;"}
  & p { margin: 0; }
  & a { ${links.linkSmallBrightPurple()} }
`;

const Link = styled("a")({
  color: "#703EFF",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 14,
  textDecoration: "none",
  "&:hover": {
    color: "#502CB5",
  },
});

const Button = styled("div")({
  marginLeft: "auto",
  "@media (max-width: 768px)": {
    marginLeft: 32,
    width: "100%",
  },
});

const Img = styled("img")({
  "@media (max-width: 768px)": {
    margin: "0 auto",
  },
});

const ErrorMessage = styled("p")({
  margin: 0,
  marginTop: 1,
});

/**
 * @param {{
 *   isUnpaidServiceFee?: boolean,
 *   hasPendingServiceFeePayment?: boolean,
 *   isUnpaidDonation?: boolean,
 *   isDelayedTicketDelivery?: boolean,
 *   isGovWalletInfo?: boolean,
 *   isError?: boolean,
 *   message?: string,
 *   orderId?: string | number,
 *   content?: string,
 * }} props
 */
const WarningInfo = ({
  isUnpaidServiceFee,
  hasPendingServiceFeePayment,
  isUnpaidDonation,
  isDelayedTicketDelivery,
  isGovWalletInfo,
  isError,
  message,
  orderId,
  content,
}) => {
 
  const title = hasPendingServiceFeePayment
      ? orderdetails_resources.PendingServiceFeePayment
      : isUnpaidServiceFee
      ? orderdetails_resources.ServiceFeeFailed
      : isUnpaidDonation
      ? orderdetails_resources.FailedDonationInstructionsHeaderText
      : isDelayedTicketDelivery
      ? orderdetails_resources.delayedDeliveryTitleText
      : "";
  const description = hasPendingServiceFeePayment
      ? orderdetails_resources.PendingServiceFeePaymentDescription
      : isUnpaidServiceFee
      ? orderdetails_resources.ServiceFeeFailedDescription
      : isUnpaidDonation
        ? orderdetails_resources.FailedDonationInstructionsBody1Text
        : isDelayedTicketDelivery
          ? orderdetails_resources.delayedDeliveryDescriptionFewDaysText
          : "";

  const handleClick = (e) => {
    e.preventDefault();
    const baseUrl = window.location.origin;
    if (isUnpaidServiceFee) {
      window.open(
        `${baseUrl}/_api/reservations/handle-service-fee/${orderId}`,
        "_blank",
      );
    }
    if (isUnpaidDonation) {
      console.log("donation");
    }
  };

  if (isError) {
    return (
      <Container $isError role="alert">
        <Content>
          <Warning color="#FC2D33" size={24} filled />
          <ErrorMessage>{message}</ErrorMessage>
        </Content>
      </Container>
    );
  }

  // Warning info without Title and Description used in reservation-page. Content provided by merchant through API with html format, so we can use dangerouslySetInnerHTML to render it.
  if (content) {
    return (
      <Container>
        <Description dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }} />
      </Container>
    );
  }

  return (
    <Container
      isDelayedTicketDelivery={isDelayedTicketDelivery}
      isUnpaidServiceFee={isUnpaidServiceFee}
      isGovWalletInfo={isGovWalletInfo}
    >
      <Content>
        {isDelayedTicketDelivery ? (
          <InfoIcon color={colors.delayedDeliveryIconColor} />
        ) : (
          <StyledIcon kind="warning-colored" />
        )}
        <div>
          <Title
            isUnpaidServiceFee={isUnpaidServiceFee}
            isDelayedTicketDelivery={isDelayedTicketDelivery}
            isUnpaidDonation={isUnpaidDonation}
          >
            {title}
          </Title>
          <Description
            isUnpaidServiceFee={isUnpaidServiceFee}
            isUnpaidDonation={isUnpaidDonation}
            isDelayedTicketDelivery={isDelayedTicketDelivery}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
          ></Description>
        </div>
      </Content>     
        <Button>
          {isUnpaidServiceFee && !hasPendingServiceFeePayment && (
            <PayButton onClick={handleClick}>
             {orderdetails_resources.Pay}
            </PayButton>
          )}
        </Button>
    </Container>
  );
};

export default WarningInfo;
